import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SmtpSettings() {
  const [cfg, setCfg] = useState({
    host: "smtp.gmail.com", port: 587, username: "", password: "",
    from_email: "", from_name: "AppRadar", use_tls: true,
  });
  const [configured, setConfigured] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tmpl, setTmpl] = useState({ subject: "", body: "" });
  const [fu, setFu] = useState({ subject: "", body: "", default_days: 4 });
  const [imap, setImap] = useState({
    host: "imap.gmail.com", port: 993, username: "", password: "",
    folder: "INBOX", use_ssl: true,
  });
  const [imapConfigured, setImapConfigured] = useState(false);
  const [cronRuns, setCronRuns] = useState([]);

  const load = async () => {
    try {
      const s = await api.get("/admin/smtp");
      if (s.data.configured) { setConfigured(true); setCfg({ ...cfg, ...s.data, password: "" }); }
    } catch { /* ignore */ }
    try {
      const t = await api.get("/admin/email-template");
      setTmpl(t.data);
    } catch { /* ignore */ }
    try {
      const f = await api.get("/admin/followup-template");
      setFu(f.data);
    } catch { /* ignore */ }
    try {
      const i = await api.get("/admin/imap");
      if (i.data.configured) { setImapConfigured(true); setImap({ ...imap, ...i.data, password: "" }); }
    } catch { /* ignore */ }
    try {
      const r = await api.get("/admin/cron/runs?limit=10");
      setCronRuns(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...cfg };
    if (!body.password) delete body.password;
    await api.put("/admin/smtp", body);
    toast.success("SMTP saved"); setConfigured(true);
  };
  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/smtp/test");
      toast.success(`Test email sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };
  const saveTmpl = async () => { await api.put("/admin/email-template", tmpl); toast.success("Template saved"); };
  const saveFu = async () => { await api.put("/admin/followup-template", fu); toast.success("Follow-up template saved"); };
  const saveImap = async () => {
    const body = { ...imap };
    if (!body.password) delete body.password;
    await api.put("/admin/imap", body);
    toast.success("IMAP saved"); setImapConfigured(true);
  };
  const runCronNow = async () => {
    await api.post("/admin/cron/test");
    toast.success("Cron tick triggered");
    setTimeout(load, 2500);
  };

  const placeholders = [
    "{developer_name}", "{developer_full_name}", "{developer_email}",
    "{developer_number}", "{developer_website}", "{developer_app_link}",
    "{app_name}", "{app_package}", "{app_link}", "{app_rating}",
    "{app_reviews}", "{app_installs}", "{app_category}",
    "{sender_name}", "{sender_email}",
  ];

  const insertToTmpl = (setter, current, ph) => setter({ ...current, body: (current.body || "") + " " + ph });

  return (
    <div className="space-y-8">
      <section>
        <h3 className="font-heading font-black text-lg mb-3">SMTP Configuration</h3>
        <div className="border border-border p-4 bg-secondary/30 space-y-3">
          <div className="text-xs text-muted-foreground">
            Google Workspace → <span className="font-mono">smtp.gmail.com:587</span> with{" "}
            <a className="underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">App Password</a>.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[["host","SMTP host"],["port","Port","number"],["username","Username"],
              ["password", configured ? "Password (blank = keep)" : "App Password","password"],
              ["from_email","From email"],["from_name","From name"]].map(([k,label,type]) => (
              <div key={k}>
                <div className="overline-label text-muted-foreground mb-1">{label}</div>
                <input type={type||"text"} value={cfg[k]} data-testid={`smtp-${k}`}
                       onChange={(e) => setCfg({...cfg,[k]: type==="number"?Number(e.target.value):e.target.value})}
                       className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm" />
              </div>
            ))}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={cfg.use_tls} onChange={(e) => setCfg({...cfg, use_tls: e.target.checked})} />
              Use STARTTLS
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="btn-save-smtp" onClick={save} className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm hover:opacity-90">Save SMTP</button>
            <button data-testid="btn-test-smtp" disabled={testing || !configured} onClick={test}
                    className="border border-border px-4 py-2 text-sm rounded-sm hover:bg-accent disabled:opacity-50">
              {testing ? "Sending…" : "Send test email"}
            </button>
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-lg mb-3">IMAP Reply Tracking</h3>
        <div className="border border-border p-4 bg-secondary/30 space-y-3">
          <div className="text-xs text-muted-foreground">
            Google Workspace → <span className="font-mono">imap.gmail.com:993</span> SSL, same account + App Password.
            Enable IMAP in Gmail settings. Cron scans every 15 min and matches replies to leads by Message-ID or sender email.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[["host","IMAP host"],["port","Port","number"],["username","Username"],
              ["password", imapConfigured ? "Password (blank = keep)" : "App Password","password"],
              ["folder","Folder (INBOX)"]].map(([k,label,type]) => (
              <div key={k}>
                <div className="overline-label text-muted-foreground mb-1">{label}</div>
                <input type={type||"text"} value={imap[k]} data-testid={`imap-${k}`}
                       onChange={(e) => setImap({...imap,[k]: type==="number"?Number(e.target.value):e.target.value})}
                       className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm" />
              </div>
            ))}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={imap.use_ssl} onChange={(e) => setImap({...imap, use_ssl: e.target.checked})} />
              Use SSL (port 993)
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="btn-save-imap" onClick={saveImap} className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm hover:opacity-90">Save IMAP</button>
            <button data-testid="btn-run-cron" onClick={runCronNow} className="border border-border px-4 py-2 text-sm rounded-sm hover:bg-accent">Run tick now</button>
          </div>
          {cronRuns.length > 0 && (
            <div className="text-[11px] font-mono border-t border-border pt-2 mt-2 max-h-40 overflow-y-auto">
              <div className="overline-label text-muted-foreground mb-1">Recent cron runs</div>
              {cronRuns.map((r) => (
                <div key={r.id} className="text-muted-foreground py-0.5">
                  {new Date(r.at).toLocaleString()} · followups: {r.followups?.sent ?? "—"}/{r.followups?.eligible ?? "—"} · replies matched: {r.replies?.matched ?? "—"}
                  {r.error && <span className="text-red-600"> · {r.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-lg mb-3">Outreach Email Template</h3>
        <div className="border border-border p-4 bg-secondary/30 space-y-3">
          <div>
            <div className="overline-label text-muted-foreground mb-1">Subject</div>
            <input value={tmpl.subject} data-testid="tmpl-subject" onChange={(e) => setTmpl({...tmpl, subject: e.target.value})}
                   className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Body</div>
            <textarea rows={10} value={tmpl.body} data-testid="tmpl-body" onChange={(e) => setTmpl({...tmpl, body: e.target.value})}
                      className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1.5">Placeholders</div>
            <div className="flex flex-wrap gap-1">
              {placeholders.map((p) => (
                <button key={p} type="button" onClick={() => insertToTmpl(setTmpl, tmpl, p)}
                        className="text-[10px] font-mono border border-border px-1.5 py-0.5 rounded-sm hover:bg-accent">{p}</button>
              ))}
            </div>
          </div>
          <button data-testid="btn-save-tmpl" onClick={saveTmpl} className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm hover:opacity-90">Save template</button>
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-lg mb-3">Follow-up Template (auto-sent if no reply)</h3>
        <div className="border border-border p-4 bg-secondary/30 space-y-3">
          <div>
            <div className="overline-label text-muted-foreground mb-1">Default delay (days)</div>
            <input type="number" min={1} max={30} value={fu.default_days}
                   onChange={(e) => setFu({...fu, default_days: Number(e.target.value)})}
                   className="w-32 border border-input bg-background px-3 py-2 text-sm rounded-sm" />
            <div className="text-[11px] text-muted-foreground mt-1">Sales can override per-lead. Fires on the next cron tick after the delay if the lead hasn't replied.</div>
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Follow-up subject</div>
            <input value={fu.subject} data-testid="fu-subject" onChange={(e) => setFu({...fu, subject: e.target.value})}
                   className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Follow-up body</div>
            <textarea rows={8} value={fu.body} data-testid="fu-body" onChange={(e) => setFu({...fu, body: e.target.value})}
                      className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1.5">Placeholders</div>
            <div className="flex flex-wrap gap-1">
              {placeholders.map((p) => (
                <button key={p} type="button" onClick={() => insertToTmpl(setFu, fu, p)}
                        className="text-[10px] font-mono border border-border px-1.5 py-0.5 rounded-sm hover:bg-accent">{p}</button>
              ))}
            </div>
          </div>
          <button data-testid="btn-save-fu" onClick={saveFu} className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm hover:opacity-90">Save follow-up template</button>
        </div>
      </section>
    </div>
  );
}
